/**
 * ProductsContainer
 * --
 */
import React from 'react';
import ProductsPresenter from './ProductsPresenter';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import MessageAlert, {
  calculateRentalMonthlyPayment,
  flattenTreeData,
  STORE_URL,
} from 'utils';
import { decrypt } from 'utils/Crypto';
import { useCreateCart, useUpdateCart } from 'hooks/CartHooks';
import { useCreateWishlist, useDeleteWishlist } from 'hooks/WishlistHooks';
import lodash from 'utils/Lodash';
import { handleSearchKeyword } from 'utils/SearchKeyword';
import CONSTANTS from 'constants';
import useGetShopProduct from 'hooks/custom/product/useGetShopProduct';

const TAP = [
  {
    key: 'DESC-comment',
    label: '판매인기순',
  },
  {
    key: 'ASC-finalPrice',
    label: '낮은가격순',
  },
  {
    key: 'DESC-finalPrice',
    label: '높은가격순',
  },
  {
    key: 'DESC-score',
    label: '상품평순',
  },
  {
    key: 'DESC-create',
    label: '등록일순',
  },
];

const ProductsContainer = (props) => {
  /* ===== ROUTE ===== */
  const { navigate, location, searchParams, screenSize } = props;
  const select = searchParams.get('category');
  const type = searchParams.get('type') ?? 'delivery';
  const classification = searchParams.get('classification');
  const keyword = searchParams.get('keyword');

  /* ===== Mutate ===== */
  const { createCart } = useCreateCart();
  const { updateCart } = useUpdateCart();
  const { createWishlist } = useCreateWishlist();
  const { deleteWishlist } = useDeleteWishlist();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const { cartList, wishlist } = useUserDataStack();
  const userDataAction = useUserDataAction();
  const products = useGetShopProduct(userInfo?.grade_id ?? null);
  const { categories, shops, settings } = useDefaultShopDataStack();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 선택 카테고리
  const [selectCategory, setSelectCategory] = React.useState(categories[0]);
  // Modal Data
  const [modalData, setModalData] = React.useState({
    type: '',
    data: null,
  });

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 장바구니/위시리스트 추가
   * --
   * @param {string} t
   * @returns
   */
  const handleSubmit = async (t = 'cart', d) => {
    let mutateData = {};
    switch (t) {
      case 'cart':
        mutateData = {
          user_id: userInfo.user_id,
          product_id: d.key,
          cart_num: d.cartNum,
          shop_information_id: d.shopKey,
          product_option: d.product_option ?? null,
          cart_type: d?.type,
          regular_cycle: d?.regular_cycle ?? null,
          max_regular_delivery_count: d?.max_regular_delivery_count ?? 0,
        };

        const filterSameProduct = cartList.filter(
          (cart) => cart.product_id === d.key
        );

        // NOTE: 장바구니에 같은 상품이 없을 경우 Create
        if (!filterSameProduct.length) {
          await handleMutate(createCart, mutateData).then((res) => {
            if (res.status !== 200) {
              MessageAlert.warning('장바구니 추가에 실패하였습니다.');
              return;
            }
            MessageAlert.success('장바구니에 추가되었습니다.');
            userDataAction.addData('cartList', res.data);
          });
        }
        // NOTE: 장바구니에 같은 상품이 있을 경우
        else {
          const isSame = cartList.find((cart) => {
            const {
              cart_num: cartListNum,
              is_fixed,
              created_at,
              updated_at,
              cart_id,
              forwarding_location_id,
              ...cartRest
            } = cart;

            const { cart_num: newValueNum, ...newValueRest } = mutateData;

            return lodash.isObjEqual(cartRest, newValueRest);
          });

          // NOTE: 같은 옵션의 상품이 있을 경우 수량 업데이트
          if (isSame) {
            mutateData = {
              ...isSame,
              cart_num: isSame.cart_num + d.cartNum,
            };

            await handleMutate(updateCart, mutateData).then((res) => {
              if (res.status !== 200) {
                MessageAlert.warning('장바구니 추가에 실패하였습니다.');
                return;
              }
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.updateData('cartList', isSame, res.data);
            });
          }
          // NOTE: 같은 옵션의 상품이 아닐 경우 장바구니 추가
          else {
            await handleMutate(createCart, mutateData).then((res) => {
              if (res.status !== 200) {
                MessageAlert.warning('장바구니 추가에 실패하였습니다.');
                return;
              }
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.addData('cartList', res.data);
            });
          }
        }

        return;
      case 'wishlist':
        mutateData = {
          user_id: userInfo.user_id,
          product_id: d.key,
        };
        await handleMutate(createWishlist, mutateData).then((res) => {
          if (res.status !== 200) {
            MessageAlert.warning('찜목록에 추가에 실패하였습니다.');
            return;
          }
          MessageAlert.success('찜목록에 추가되었습니다.');
          userDataAction.addData('wishlist', res.data);
        });
        return;
      case 'remove':
        mutateData = wishlist.find(
          (wish) => wish.product_id === d.key && wish.type === 'WISH'
        );
        await handleMutate(deleteWishlist, mutateData.wishlist_id).then(
          (res) => {
            if (res.status !== 200) {
              MessageAlert.warning('찜목록에서 삭제하는데 실패하였습니다.');
              return;
            }
            MessageAlert.success('찜목록에서 삭제되었습니다.');
            userDataAction.removeData('wishlist', 'wishlist_id', mutateData);
          }
        );
        return;
      default:
        MessageAlert.warning(
          `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
        );
        return;
    }
  };

  /**
   * Mutate Function 실행
   * --
   * @param {function} mutateFn
   * @param {object} mutate
   * @returns
   */
  const handleMutate = async (mutateFn, mutate) => {
    try {
      const res = await mutateFn(mutate);

      setModalData({ type: '', data: null });
      return res;
    } catch (err) {
      console.error('[Product handleMutate] Error !! ', err);
      // MessageAlert.error(
      //   `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
      // );
      throw err;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 카테고리 플랫 정제
   * --
   */
  const FlattenCategories = React.useMemo(() => {
    return flattenTreeData(categories);
  }, [categories]);

  /**
   * 상품 목록 정제
   * --
   */
  const ProductList = React.useMemo(() => {
    const KEYS = new Set([]);
    const newProducts = products
      ?.filter((product) => {
        const t = type.toLocaleUpperCase();
        if (t === 'DELIVERY' || t === 'SUBSCRIPTION' || t === 'RENTAL') {
          return product.type === t;
        } else if (type === 'best' || type === 'new' || type === 'present') {
          return product[`is_${type}`] === 'Y';
        } else {
          return false;
        }
      })
      .map((product) => {
        const {
          product_name,
          num,
          price,
          product_status,
          product_id,
          shop_information_id,
          created_at,
          average_score,
          review_count,
          tag,
          product_option,
          type: delivery_type,
          packaging_method,
          regular_information,
          is_best,
          is_present,
          is_new,

          thumbnail,
          shop_information_name,
          product_category_key,
          product_category_name,
          rental_default_option,
          apply_policy_info,
          final_discount_price,
          final_reserve_price,
          final_discount_rate,
        } = product;

        // 태그 추출
        const tagList = tag.map((tag) => ({
          id: tag.tag_id,
          label: tag.tag_name,
        }));

        //검색 필터 태그용
        const tagsKeys = tag?.reduce((acc, tag, index) => {
          const tagKeyName = `tagKey_${index}`;
          KEYS.add(tagKeyName);
          return {
            [tagKeyName]: tag?.tag_name,
            ...acc,
          };
        }, {});

        const productPrice =
          delivery_type === 'RENTAL'
            ? calculateRentalMonthlyPayment(price, rental_default_option)
            : price;

        return {
          key: product_id,
          category: product_category_key,
          title: product_name,
          price: productPrice,
          isSoldOut: num === 0 || product_status === 'SOLDOUT' ? 'Y' : 'N',
          src: !thumbnail ? null : `${STORE_URL}/${thumbnail.file_url}`,
          tags: tagList,
          brand: shop_information_name,
          shopKey: shop_information_id,

          discount: final_discount_rate,
          discount_type: 'R',
          finalPrice: productPrice - final_discount_price,
          finalReserve: final_reserve_price,
          policies: apply_policy_info,

          isWish: wishlist.some(
            (wish) => wish.product_id === product_id && wish.type === 'WISH'
          ),
          create: created_at,
          score: average_score,
          comment: review_count,
          options: product_option,
          type: delivery_type,

          packaging: packaging_method,
          regularCycle:
            delivery_type === 'SUBSCRIPTION'
              ? regular_information?.regular_cycle
              : null,
          maxRegularCount:
            delivery_type === 'SUBSCRIPTION'
              ? regular_information?.max_regular_delivery_count
              : null,
          rentalInfo: rental_default_option,
          isBest: is_best,
          isNew: is_new,
          isPresent: is_present,
          category_name: product_category_name,
          ...tagsKeys,
        };
      });
    let productData = newProducts;
    if (classification) {
      const classType = classification.toLocaleUpperCase();
      switch (classType) {
        case 'PROMOTION':
          productData = newProducts.filter(
            (product) =>
              product.policy && product.policy.policy_type === 'PROMOTION'
          );
          break;
        case 'BRAND':
          const brandList = shops.filter((shop) => shop.is_brand === 'Y');

          productData = newProducts.filter((product) =>
            brandList.some(
              (brand) => brand.shop_information_id === product.shopKey
            )
          );
          break;
        default:
          break;
      }
    }
    return keyword
      ? handleSearchKeyword(productData, keyword, [
          'title',
          'category_name',
          ...Array.from(KEYS),
        ])
      : productData;
  }, [products, shops, wishlist, type, classification, keyword]);

  /**
   * 선물하기 카테고리 정제
   * --
   */
  const PresentCategories = React.useMemo(() => {
    if (type !== 'present') return [];

    const filterCategories = FlattenCategories?.filter((category) =>
      ProductList?.some((product) => {
        const { category: cate } = product;
        const split = cate?.split('-');

        return split?.includes(category?.key);
      })
    );

    return filterCategories;
  }, [FlattenCategories, ProductList, type]);

  /**
   * 카테고리 정제
   * --
   */
  const CategoryList = React.useMemo(() => {
    const mapping = (cate) => {
      const { key, product_category_name, children = [] } = cate;

      return {
        key: key,
        title: product_category_name,
        children: children.map((child) => mapping(child)),
      };
    };

    // 사업자 카테고리 필터
    const filteredCategory = categories.filter((category) => {
      const isBusiness =
        category.product_category_name === CONSTANTS.BUSINESS_CATEGORY_NAME;

      if (isBusiness) {
        if (userInfo && userInfo?.business_level === 2) return true;
        else return false;
      } else {
        return true;
      }
    });

    const newCategories = filteredCategory.map((cate) => mapping(cate));

    return newCategories ?? [];
  }, [categories, userInfo]);

  /**
   * 필터 아이템 정제
   * --
   */
  const FilterItems = React.useMemo(() => {
    const filterShop =
      classification === 'brand'
        ? shops.filter((shop) => shop.is_brand === 'Y')
        : shops;
    const brandFilterItems = filterShop.map((shop) => ({
      label: shop.shop_information_name,
      value: shop.shop_information_id,
    }));

    return [
      {
        title: '포장타입',
        options: [
          { label: '일반', value: 'GENERAL' },
          { label: '냉장', value: 'REFRIGERATION' },
          { label: '냉동', value: 'FREEZING' },
        ],
        key: 'packaging',
        type: 'include',
      },
      {
        title: '가격',
        options: [
          { label: '10,000원 미만', value: [0, 10000] },
          { label: '10,000원 ~ 50,000원', value: [10000, 50000] },
          { label: '50,000원 ~ 100,000원', value: [50000, 100000] },
          { label: '100,000원 이상', value: [100000, 9999999999] },
        ],
        key: 'finalPrice',
        type: 'range',
      },
      {
        title: '브랜드',
        options: brandFilterItems,
        multiple: true,
        key: 'shopKey',
        type: 'include',
      },
    ];
  }, [shops, classification]);

  /**
   * 브랜드 데이터 정제
   * --
   */
  const BrandList = React.useMemo(() => {
    if (classification !== 'brand') return [];

    const filterBrands = shops.filter((shop) => shop.is_brand === 'Y');

    const newBrandList = filterBrands.map((brand) => {
      const { shop_information_file } = brand;

      const thumbnail = shop_information_file.find(
        (file) => file.type === 'THUMBNAIL'
      );

      return {
        ...brand,
        thumbnail: thumbnail ? `${STORE_URL}/${thumbnail.file_url}` : null,
      };
    });

    return newBrandList;
  }, [classification, shops]);

  /**
   * 정렬 아이템 정제
   * --
   */
  const SortItems = React.useMemo(() => {
    if (!settings) return TAP;

    const { product_standard, product_order_by } = settings;

    let standard = '';
    switch (product_standard) {
      case 'NAME':
        standard = `${product_order_by}-title`;
        break;
      case 'PRICE':
        standard = `${product_order_by}-price`;
        break;
      case 'REGISTER':
        standard = `${product_order_by}-create`;
        break;
      default:
        standard = `${product_order_by}-finalPrice`;
        break;
    }

    return [
      {
        key: standard,
        label: '추천순',
      },
      ...TAP,
    ];
  }, [settings]);

  /**
   * Query Key 카테고리 정제
   * --
   */
  React.useEffect(() => {
    const decodeKey = decrypt(decodeURIComponent(select));

    const mapCategories = new Map(
      FlattenCategories.map((category) => [
        category.product_category_id,
        category,
      ])
    );
    const findCategory = mapCategories.get(Number(decodeKey));

    setSelectCategory(findCategory);
  }, [select, FlattenCategories]);

  /* ===== RENDER ===== */
  return (
    <>
      <ProductsPresenter
        navigate={navigate}
        location={location}
        user={userInfo}
        products={ProductList}
        categories={PresentCategories}
        categoryList={CategoryList}
        selectedCategory={selectCategory}
        modalData={modalData}
        cartList={cartList}
        filterItems={FilterItems}
        type={type}
        classification={classification}
        brand={BrandList}
        onSubmit={handleSubmit}
        setModalData={setModalData}
        sortItems={SortItems}
        screenSize={screenSize}
      />
    </>
  );
};

export default ProductsContainer;
